import { showToast, toast } from '@crowdex/ui/components/toaster';
import { useCallback, useEffect, useRef } from 'react';
import { findArtifactTarget } from '@/features/apps/dock-artifact-target';
import { onStreamedEvent } from '@/features/notifications/notification-feed';
import { CORE_LIVE_VIEW } from '@/features/plugins/core-panels';
import type { PluginPanel } from '@/features/plugins/plugin-workbench';
import { railPanels } from '@/features/plugins/plugin-workbench';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import { dockApp, dockAppIn } from '@/lib/desktop-shell/app-panels';
import { useConversationOpener } from '@/lib/desktop-shell/conversation-opener';
import { joinSidebar } from '@/lib/desktop-shell/joined-conversations';
import { dockArtifact } from '@/lib/desktop-shell/open-artifact';
import { useDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import type { SidePanelView } from '@/lib/side-panel/side-panel-items';
import { useLiveCallContext } from './live-call';
import { useOpenLive } from './live-panel';

/** The core's event of the assistant showing the user something (its `show` tool). */
const SHOW_EVENT = 'crowdex/assistant.show';
/**
 * The core's event of the assistant calling the user, one per state of a ring; one told in the
 * call may show a file.
 */
export const RING_EVENT = 'crowdex/assistant.ring';

/** How long a conversation that just joined the sidebar takes to be listed, to open it. */
const OPEN_ATTEMPTS = 20;
const OPEN_RETRY_MS = 100;

/** The rail app the assistant named: by its title, plugin or id, in any case. */
export function namedPanel(panels: readonly PluginPanel[], name: string): PluginPanel | undefined {
  const wanted = name.trim().toLowerCase();
  return panels.find((panel) =>
    [panel.title, panel.plugin, panel.id, `${panel.plugin}/${panel.id}`, panel.view].some(
      (candidate) => candidate?.toLowerCase() === wanted,
    ),
  );
}

/** What the assistant shows: a conversation, a file of its workspace beside it, an app. */
export type ShowTarget = { conversation?: string; file?: string; app?: string; item?: string };

/** What a `show` event (or a told ring that shows a file) asks to show. */
export function showTarget(event: { fields: Record<string, unknown> }): ShowTarget {
  const text = (name: string) =>
    typeof event.fields[name] === 'string' ? (event.fields[name] as string) : undefined;
  const conversation = text('conversation');
  const file = text('file');
  const app = text('app');
  const item = text('item');
  return {
    ...(conversation === undefined ? {} : { conversation }),
    ...(file === undefined ? {} : { file }),
    ...(app === undefined ? {} : { app }),
    ...(app === undefined || item === undefined ? {} : { item }),
  };
}

/**
 * Shows something on this page (docs/redesign-implementation-plan.md §6.1): a conversation,
 * opened (it joins the sidebar first); a file of its workspace, docked in its side panel (the
 * artifact's tab); an app, docked there too, on `item` (an item of its sidebar) when given. From a
 * conversation an app only docks, never full size. Out of a call (`quiet`), what is for a
 * conversation that is not on screen is prepared in that conversation's panel instead of taking
 * the screen, and a toast says where, with Open.
 */
export function useShowInPage(): (
  target: ShowTarget,
  options?: { quiet?: boolean },
) => Promise<void> {
  const store = useDesktopShellStoreApi();
  const opener = useConversationOpener();
  const openLive = useOpenLive();
  const workbench = useWorkbench();
  const latest = useRef({ opener, openLive, workbench });
  latest.current = { opener, openLive, workbench };
  return useCallback(
    async ({ conversation, file, app, item }: ShowTarget, options: { quiet?: boolean } = {}) => {
      const panel =
        app === undefined ? undefined : namedPanel(railPanels(latest.current.workbench), app);
      // What docks: the file's artifact, else the app (on its item).
      let view: SidePanelView | null = null;
      if (file !== undefined && conversation !== undefined) {
        const artifact = await findArtifactTarget({
          ...(panel?.appView === undefined ? {} : { plugin: panel.appView.plugin }),
          space: 'workspace',
          path: file,
          conversation,
        });
        if (artifact === undefined)
          toast.error('Artifact', `${file} is not an artifact of that conversation.`);
        else view = `artifact:${artifact.id}`;
      } else if (panel !== undefined && panel.view !== CORE_LIVE_VIEW) view = panel.view;
      const target =
        item === undefined || panel?.appView === undefined
          ? undefined
          : { item, section: panel.rail?.sidebars?.[0]?.id };
      if (
        options.quiet === true &&
        conversation !== undefined &&
        conversation !== store.getState().chatId &&
        view !== null
      ) {
        dockAppIn(store, conversation, view, target);
        const title =
          store.getState().syncedThreads.find((thread) => thread.id === conversation)?.title ??
          'its conversation';
        showToast({
          title: `${panel?.title ?? 'It'} is ready in ${title}`,
          tone: 'info',
          action: {
            label: 'Open',
            onSelect: () => void latest.current.opener?.openConversation(conversation),
          },
        });
        return;
      }
      if (conversation !== undefined && latest.current.opener !== null) {
        await joinSidebar(store, conversation);
        for (let attempt = 0; attempt < OPEN_ATTEMPTS; attempt += 1) {
          if (await latest.current.opener?.openConversation(conversation)) break;
          await new Promise((resolve) => setTimeout(resolve, OPEN_RETRY_MS));
        }
      }
      // A file is its artifact's tab, as every way to an artifact from a conversation docks it.
      if (view?.startsWith('artifact:')) dockArtifact(store, view.slice('artifact:'.length));
      else if (view !== null) dockApp(store, view, target);
      else if (panel?.view === CORE_LIVE_VIEW) await latest.current.openLive();
    },
    [store],
  );
}

/**
 * Shows what the assistant asked to (`show`), and the file a ring told in the call shows. The
 * page in the call does, else one the user looks at; the others leave their screen alone.
 */
function useAssistantShow(): void {
  const show = useShowInPage();
  const inCall = useLiveCallContext()?.call.status !== 'idle';
  const latest = useRef({ show, inCall });
  latest.current = { show, inCall };
  useEffect(
    () =>
      onStreamedEvent((event) => {
        const told =
          event.type === RING_EVENT &&
          event.fields['state'] === 'told' &&
          typeof event.fields['file'] === 'string';
        if (event.type !== SHOW_EVENT && !told) return;
        if (told && !latest.current.inCall) return;
        if (!latest.current.inCall && document.visibilityState !== 'visible') return;
        void latest.current
          .show(showTarget(event), { quiet: !latest.current.inCall })
          .catch(() => undefined);
      }),
    [],
  );
}

/** Mounts `useAssistantShow` once, in the shell. */
export function AssistantShow(): null {
  useAssistantShow();
  return null;
}
